/**
 * 종목 정보 카드 공통 컴포넌트 — PER/PBR/ROE/시가총액/52주 최고·최저
 * 시장인사이트 페이지 / 종목 상세 드로어 공용.
 * infoQ를 내장 — 페이지에서 동일 키(["market","info",symbol,overseas]) 쿼리와 캐시 공유.
 */
import { Card, Empty, Spin, Statistic } from "antd";
import { useQuery } from "@tanstack/react-query";
import { marketApi } from "src/api/market";
import { currency } from "src/lib/format";

interface StockInfoCardProps {
  symbol: string;
  overseas?: boolean;
  /** 그리드 열 수 (드로어 3 / 시장인사이트 2) */
  columns?: number;
  /** Statistic 폰트 크기 (기본 13) */
  fontSize?: number;
  enabled?: boolean;
  marginBottom?: number;
}

const StockInfoCard = ({
  symbol,
  overseas = false,
  columns = 3,
  fontSize = 13,
  enabled = true,
  marginBottom = 12,
}: StockInfoCardProps) => {
  const infoQ = useQuery({
    queryKey: ["market", "info", symbol, overseas],
    queryFn: () => marketApi.getInfo(symbol, overseas),
    enabled: enabled && !!symbol,
    staleTime: 10 * 60_000,
    retry: 1,
  });

  const info = infoQ.data;

  return (
    <Card size="small" title="종목 정보" style={{ marginBottom }}>
      {infoQ.isLoading ? (
        <Spin style={{ display: "block", margin: "30px auto" }} />
      ) : infoQ.isError ? (
        <Empty description="종목 정보를 불러올 수 없습니다" style={{ margin: "30px 0" }} />
      ) : info && (info.per || info.pbr || info.w52_high) ? (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 6 }}>
          <Statistic title="PER" value={info.per?.toFixed(1) ?? "-"} valueStyle={{ fontSize }} />
          <Statistic title="PBR" value={info.pbr?.toFixed(2) ?? "-"} valueStyle={{ fontSize }} />
          <Statistic title="ROE" value={info.roe != null ? `${info.roe.toFixed(1)}%` : "-"} valueStyle={{ fontSize }} />
          <Statistic
            title="시가총액"
            value={info.market_cap ? currency(info.market_cap * (overseas ? 1 : 1000000)) : "-"}
            valueStyle={{ fontSize }}
          />
          <Statistic title="52주 최고" value={info.w52_high?.toLocaleString() ?? "-"} valueStyle={{ fontSize }} />
          <Statistic title="52주 최저" value={info.w52_low?.toLocaleString() ?? "-"} valueStyle={{ fontSize }} />
        </div>
      ) : (
        <Empty description="정보 없음 (ETF 등)" style={{ margin: "30px 0" }} />
      )}
    </Card>
  );
};

export default StockInfoCard;
